import type { ReactNode } from "react";
import type { Filters, Location, TrendPoint } from "../types";

export function StatCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

export function StatusTag({ status }: { status: string }) {
  return <span className={`tag ${status}`}>{status}</span>;
}

export function Loading({ what = "data" }: { what?: string }) {
  return <div className="notice">Loading {what}…</div>;
}

export function ErrorNote({ message }: { message: string }) {
  return <div className="notice error">⚠ {message}</div>;
}

export function secsToHHMM(s: number | null): string {
  if (s === null) return "—";
  const neg = s < 0;
  const abs = Math.abs(s);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  return `${neg ? "-" : ""}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function FilterBar({
  filters, locations, onChange, extra,
}: {
  filters: Filters;
  locations: Location[];
  onChange: (f: Filters) => void;
  extra?: ReactNode;
}) {
  const set = (k: keyof Filters, v: string) => onChange({ ...filters, [k]: v || undefined });
  return (
    <div className="filters">
      <div className="field">
        <label>Location</label>
        <select value={filters.location ?? ""} onChange={(e) => set("location", e.target.value)}>
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>From</label>
        <input type="date" value={filters.from ?? ""} onChange={(e) => set("from", e.target.value)} />
      </div>
      <div className="field">
        <label>To</label>
        <input type="date" value={filters.to ?? ""} onChange={(e) => set("to", e.target.value)} />
      </div>
      <div className="field">
        <label>Department</label>
        <input placeholder="e.g. Employees" value={filters.department ?? ""}
               onChange={(e) => set("department", e.target.value)} />
      </div>
      {extra}
    </div>
  );
}

// Lightweight dependency-free trend chart (line for inside hours per period).
export function TrendChart({ points }: { points: TrendPoint[] }) {
  if (points.length === 0) return <div className="notice">No data in range.</div>;
  const w = 720, h = 180, pad = 28;
  const xs = points.map((p) => (p.date ?? p.month) as string);
  const ys = points.map((p) => p.inside_seconds / 3600);
  const maxY = Math.max(...ys, 1);
  const stepX = (w - pad * 2) / Math.max(points.length - 1, 1);
  const px = (i: number) => pad + i * stepX;
  const py = (v: number) => h - pad - (v / maxY) * (h - pad * 2);
  const path = ys.map((v, i) => `${i === 0 ? "M" : "L"}${px(i)},${py(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img"
         aria-label="Inside-office hours trend">
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="var(--border)" />
      <path d={path} fill="none" stroke="var(--brand)" strokeWidth={2} />
      {ys.map((v, i) => <circle key={i} cx={px(i)} cy={py(v)} r={3} fill="var(--brand)" />)}
      {xs.map((label, i) =>
        i % Math.ceil(points.length / 8) === 0 ? (
          <text key={i} x={px(i)} y={h - 8} fontSize="10" textAnchor="middle" fill="var(--muted)">
            {label.slice(5)}
          </text>
        ) : null)}
      <text x={pad} y={14} fontSize="10" fill="var(--muted)">{maxY.toFixed(0)}h</text>
    </svg>
  );
}
